import { toast } from 'sonner';

import { materialApi } from '@/api/modules/material/api';
import {
  UPLOAD_TASK_ITEM_STATUS,
  UPLOAD_TASK_TYPE,
  uploadTaskApi,
  type UpdateUploadTaskItemDto,
} from '@/api/modules/material/uploadTask';
import { queryClient } from '@/lib/queryClient';
import { computeDuplicateUids } from '@/pages/AssetCenter/MaterialLibrary/components/duplicateFilter';
import { uploadClient } from '../core/uploadClient';
import { invalidateMaterialLibraryQueries } from '../core/materialLibraryCache';
import { buildReconcilePlan, isFileRetryable } from '../core/reconcile';
import { buildUploadTaskFiles, buildUploadTaskFolders } from '../core/taskPayload';
import { buildSaveMaterialInfo } from '@/pages/AssetCenter/MaterialLibrary/components/MaterialUploadSheet/utils';
import type {
  CreateUploadTaskParams,
  UploadTask,
  UploadTaskDuplicateItem,
  UploadTaskFile,
} from '../core/types';
import { useUploadTaskStore, collectSubmittableInfos } from '../store/uploadTaskStore';

/**
 * 上传任务编排层
 *
 * 完整流程：
 *   ① 抽屉表单校验通过 → `createUploadTask()`
 *        · 先调 `POST /upload/task/create`（只带 clientFileId + fileName），
 *          后端预建目录树并按 `clientFileId` 回传每个明细的 `itemId` / `folderId`
 *        · 用回传值补全本地任务（`uid` 就是 `clientFileId`），抽屉立即关闭
 *        · `uploadClient.enqueue()` 开始直传 OSS，进度写本地 store
 *   ② 全部文件 settle → `submitTask()`
 *        · 按明细的 `targetFolderId` 分组调既有 `POST /material/save`（文件夹任务**不再**走 folder/batchUpload）
 *        · 成功后调 `POST /upload/task/status/update` **按明细增量**回写状态
 *   ③ 异常兜底（浏览器强关 / 关机导致 ② 没跑完）→ `reconcileUploadTasks()`
 *        · 有成功素材 → 补一次入库 + 回写；没有 → 全部回写失败
 *
 * **id 一律按 string 处理**：`taskId` / `itemId` / `folderId` 都是后端雪花 ID，
 * 前端不做 `Number()` 转换（会丢精度）。
 */

/** 回写失败时的重试间隔（契约要求前端重试；接口本身幂等，重复上报会被后端跳过） */
const REPORT_RETRY_DELAYS = [500, 1500, 3000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getTask = (taskId: string) =>
  useUploadTaskStore.getState().tasks.find((item) => item.taskId === taskId);

/**
 * 把成功文件的 infos 映射成 save 接口需要的结构。
 *
 * `/material/save` 的 `SaveMaterialInfo` 已由 `resolution` 字符串改为 `width` / `height` 数字，
 * 必须走统一的组装函数，否则异步上传完成后素材宽高不会落库。
 */
const buildSaveInfos = (files: UploadTaskFile[]) =>
  files.map((file) => buildSaveMaterialInfo(file.info!));

/**
 * 失效上传任务相关的查询缓存（列表分页 + 详情）。
 *
 * **必须在每次成功回写后调用**：任务状态与计数只有服务端知道，
 * 不失效的话列表会一直停在缓存里的「处理中」，直到用户手动刷新页面。
 */
const invalidateUploadTaskQueries = () => {
  void queryClient.invalidateQueries({ queryKey: ['upload-task', 'page'] });
  void queryClient.invalidateQueries({ queryKey: ['upload-task', 'detail'] });
};

/** 从接口响应中提取 duplicates */
const extractDuplicates = (result: unknown): UploadTaskDuplicateItem[] => {
  const data = (result as { data?: { duplicates?: UploadTaskDuplicateItem[] } })?.data;
  return data?.duplicates ?? [];
};

/**
 * 创建批次任务并开始后台上传。
 *
 * 先把明细清单交给后端建任务（此时文件还没上传 OSS），
 * 用后端回传的 `itemId` / `folderId` 补全本地任务，再开始直传。
 */
export const createUploadTask = async ({
  bizType,
  title,
  snapshot,
  files,
  folderPaths = [],
}: CreateUploadTaskParams): Promise<string> => {
  const taskType = bizType === 'FOLDER' ? UPLOAD_TASK_TYPE.FOLDER : UPLOAD_TASK_TYPE.MATERIAL;

  /**
   * 后端要求 `folderCategory` 必填（personal / public / enterprise / department）。
   * 它由「上传目标文件夹」反查得到，取不到说明目标文件夹没选或分类树未就绪 —— 直接失败，
   * 避免把非法入参发给后端。
   */
  const folderCategory = snapshot.folderCategory;
  if (!folderCategory) {
    throw new Error('未识别到上传文件夹分类，请重新选择上传文件夹');
  }

  const created = await uploadTaskApi.create({
    taskType,
    folderId: snapshot.folderId,
    // 设计师与创意人二者至少一个（抽屉提交校验保证），空串不落库
    designerId: snapshot.designerId || undefined,
    creativeId: snapshot.creativeId || undefined,
    tagIds: snapshot.tagIds?.length ? snapshot.tagIds : undefined,
    folderCategory,
    // 素材类型传扁平 files，文件夹类型传目录树（空目录用 files: [] 表达）
    ...(taskType === UPLOAD_TASK_TYPE.FOLDER
      ? { folders: buildUploadTaskFolders(files, folderPaths) }
      : { files: buildUploadTaskFiles(files) }),
  });

  const resp = created.data;
  const taskId = resp?.taskId;
  if (!taskId) {
    throw new Error(created.msg || '创建上传任务失败');
  }

  /** 后端按 clientFileId（= 本地 uid）回传每个明细的主键与落库目标 */
  const briefMap = new Map(resp.items.map((item) => [item.clientFileId, item]));
  const mergedFiles = files.map((file) => {
    const brief = briefMap.get(file.uid);
    return brief ? { ...file, itemId: brief.itemId, targetFolderId: brief.folderId } : file;
  });

  /** 只选空文件夹（明细 0 条）→ 后端创建即「已完成」，前端不需要上传也不需要回写 */
  const isEmptyTask = mergedFiles.length === 0;

  const task: UploadTask = {
    taskId,
    bizType,
    title,
    createdAt: Date.now(),
    status: isEmptyTask ? 'success' : 'uploading',
    snapshot: {
      ...snapshot,
      // 设定以后端回显为准（save 时原样携带，保证台账与落库一致）
      designerId: resp.designerId || snapshot.designerId,
      creativeId: resp.creativeId ?? snapshot.creativeId,
      tagIds: resp.tagIds ?? snapshot.tagIds,
      folderCategory: resp.folderCategory ?? snapshot.folderCategory,
    },
    files: mergedFiles,
    folderPaths,
    progress: {
      total: mergedFiles.length,
      success: 0,
      failed: 0,
      percent: isEmptyTask ? 100 : 0,
    },
    submittedUids: [],
    fileCount: mergedFiles.length,
    serverSynced: isEmptyTask,
  };

  useUploadTaskStore.getState().addTask(task);
  // 新任务立刻出现在列表里（不依赖页面上的本地任务监听）
  invalidateUploadTaskQueries();

  if (isEmptyTask) return taskId;

  uploadClient.enqueue({
    files: mergedFiles,
    callbacks: {
      onFilePatch: (uid, patch) => {
        useUploadTaskStore.getState().patchFile(taskId, uid, patch);
      },
      onFileSettled: (uid) => {
        handleFileSettled(taskId, uid);
      },
    },
  });

  return taskId;
};

/** 已触发提交的批次，避免多次 settle 重复调接口 */
const submitTriggered = new Set<string>();

/** 单文件 settle 后检查整批是否全部结束 */
const handleFileSettled = (taskId: string, _uid: string) => {
  const task = getTask(taskId);
  if (!task) return;
  if (task.status !== 'uploading') {
    // 批次已在 saving / 终态：用于「批次已提交后单文件重试成功 → 追加提交」的场景
    void submitTask(taskId, { append: true });
    return;
  }
  const allSettled = task.files.every(
    (file) => file.status === 'success' || file.status === 'error' || file.status === 'canceled'
  );
  if (!allSettled) return;
  void submitTask(taskId, { append: false });
};

interface SubmitOptions {
  /** true 表示「追加提交」：只提交尚未提交过的成功项（重试成功后使用） */
  append: boolean;
  /** 静默模式（reconcile 时使用），不弹 toast */
  silent?: boolean;
}

/** `reportTaskItems` 的可选项 */
export interface ReportTaskItemsOptions {
  /**
   * 只上报这些明细（按本地 `uid`）。
   *
   * 用于「重试前补回写」：只把即将重试的失败明细标成失败，
   * 不误伤同批次里还在上传中（本地尚未 settle）的明细。
   */
  onlyUids?: string[];
}

/**
 * 按明细增量回写最终状态。
 *
 * - 成功明细：`itemStatus = 1` + ossKey + md5/宽高/时长
 * - 失败明细：`itemStatus = 2` + failReason
 *
 * 幂等：本地已 `reported` 的明细不再上报；接口侧也会静默跳过重复 / 迟到上报。
 * 回写失败会按 `REPORT_RETRY_DELAYS` 重试，仍失败则留给下次对账兜底。
 */
export const reportTaskItems = async (
  taskId: string,
  options: ReportTaskItemsOptions = {}
): Promise<boolean> => {
  const task = getTask(taskId);
  if (!task) return false;

  const store = useUploadTaskStore.getState();
  const only = options.onlyUids ? new Set(options.onlyUids) : null;
  const pending = task.files.filter(
    (file) => !file.reported && file.itemId && (!only || only.has(file.uid))
  );

  /**
   * 全部明细都已上报才算对账完成（而不是「本次没有待上报的」——
   * 指定 `onlyUids` 时两者的区别会体现出来）。
   */
  const markSyncedIfAllReported = () => {
    const latest = getTask(taskId);
    if (latest && latest.files.every((file) => file.reported)) {
      if (!latest.serverSynced) store.patchTask(taskId, { serverSynced: true });
    }
  };

  if (pending.length === 0) {
    // 没有待上报的明细：补一次幂等标记，reconcile 不再重复处理
    markSyncedIfAllReported();
    return true;
  }

  const items: UpdateUploadTaskItemDto[] = pending.map((file) =>
    file.status === 'success'
      ? {
          itemId: file.itemId as string,
          itemStatus: UPLOAD_TASK_ITEM_STATUS.SUCCESS,
          ossKey: file.info?.ossKey,
          fileMd5: file.info?.md5,
          width: file.info?.width ? Number(file.info.width) : undefined,
          height: file.info?.height ? Number(file.info.height) : undefined,
          duration: file.info?.duration,
        }
      : {
          itemId: file.itemId as string,
          itemStatus: UPLOAD_TASK_ITEM_STATUS.FAILED,
          failReason: file.errorMessage || '上传失败',
        }
  );

  for (let attempt = 0; ; attempt += 1) {
    try {
      await uploadTaskApi.updateStatus({ taskId, items });
      pending.forEach((file) => store.patchFile(taskId, file.uid, { reported: true }));
      markSyncedIfAllReported();
      // 服务端的状态 / 计数已经变了 → 让列表与详情缓存失效，否则列表还显示「处理中」
      invalidateUploadTaskQueries();
      return true;
    } catch (error) {
      const delay = REPORT_RETRY_DELAYS[attempt];
      if (delay === undefined) {
        console.warn('[material-upload] 回写明细状态失败，留给下次对账重试', error);
        return false;
      }
      await sleep(delay);
    }
  }
};

/**
 * 调既有入库接口提交批次（按明细的落库目标文件夹分组），成功后按明细回写状态。
 * 失败项不阻塞——只提交成功项。
 */
export const submitTask = async (taskId: string, options: SubmitOptions) => {
  const store = useUploadTaskStore.getState();
  const task = getTask(taskId);
  if (!task) return;

  const duplicateUids = computeDuplicateUids(
    task.files.map((file) => ({
      uid: file.uid,
      status: file.status,
      info: file.info,
      folderPath: file.folderPath,
    })),
    task.snapshot.checkDuplicate,
    (item) => item.folderPath || ''
  );

  const submittable = collectSubmittableInfos(task.files, duplicateUids, task.submittedUids);

  if (submittable.length === 0) {
    if (!options.append) {
      /**
       * 没有任何可入库素材。若失败项在本次会话内还可重传
       * （典型场景：「生成上传路径」接口超时导致整批文件失败），
       * 提示语要指向「重试」而不是「没有成功上传的文件」。
       */
      const hasRetryableFailure = task.files.some(isFileRetryable);
      const message = hasRetryableFailure
        ? '文件上传失败，可在任务明细中重试'
        : '没有成功上传的文件';
      store.patchTask(taskId, { status: 'failed', errorMessage: message });
      if (!options.silent) toast.error(message);
    }
    // 即使没有可入库内容也要回写，否则服务端任务会一直停在「处理中」
    await reportTaskItems(taskId);
    return;
  }

  // 追加提交时任务本已是终态，不要回退 UI；首次提交则进入 saving
  if (!options.append) {
    store.patchTask(taskId, { status: 'saving' });
  } else if (submitTriggered.has(taskId)) {
    return;
  }
  submitTriggered.add(taskId);

  try {
    /**
     * 按明细的落库目标文件夹分组：
     * 素材类型只有一组（上传目标文件夹），文件夹类型每个预建子目录一组。
     * 契约明确要求**统一走 `/material/save`**，不再调 `/folder/batchUpload`。
     */
    const groups = new Map<string, UploadTaskFile[]>();
    for (const file of submittable) {
      const folderId = file.targetFolderId || task.snapshot.folderId || '';
      const list = groups.get(folderId) ?? [];
      list.push(file);
      groups.set(folderId, list);
    }

    let duplicates: UploadTaskDuplicateItem[] = [];
    for (const [folderId, groupFiles] of groups) {
      const result = await materialApi.save({
        folderId,
        // 设计师与创意人二者至少一个；空串按未填处理，不传给后端
        designerId: task.snapshot.designerId || undefined,
        creativeId: task.snapshot.creativeId || undefined,
        tagIds: task.snapshot.tagIds?.length ? task.snapshot.tagIds : undefined,
        infos: buildSaveInfos(groupFiles),
        checkDuplicate: task.snapshot.checkDuplicate,
      });
      duplicates = [...duplicates, ...extractDuplicates(result)];
    }

    // 入库成功 → 素材库列表（含 KeepAlive 缓存的页面）必须失效，否则那边还是旧列表
    invalidateMaterialLibraryQueries();
    void queryClient.invalidateQueries({ queryKey: ['folder', 'tree'] });

    const submittedUids = [...task.submittedUids, ...submittable.map((file) => file.uid)];
    const mergedDuplicates = [...(task.duplicates ?? []), ...duplicates];

    const latest = getTask(taskId);
    const remaining = (latest?.files ?? []).filter((file) => file.status !== 'success');
    const hasRetryableFailure = remaining.some(isFileRetryable);

    store.patchTask(taskId, {
      status: remaining.length ? 'failed' : 'success',
      submittedUids,
      duplicates: mergedDuplicates.length ? mergedDuplicates : undefined,
      errorMessage: remaining.length
        ? hasRetryableFailure
          ? '部分文件上传失败，可在任务明细中重试'
          : '部分文件上传失败'
        : undefined,
    });

    // 入库成功 → 按明细回写最终状态
    await reportTaskItems(taskId);

    if (!options.append && !options.silent) {
      toast.success('上传完成');
    }
  } catch (error) {
    const message = (error as { message?: string })?.message || '保存失败';
    // 入库失败：不回写明细状态，任务停在「处理中」，可通过「重试」重新入库
    store.patchTask(taskId, { status: 'failed', errorMessage: message });
    if (!options.silent) toast.error(message);
  } finally {
    submitTriggered.delete(taskId);
  }
};

/**
 * 重试指定明细：先让后端把明细置回待上传并回传落库目标与设定快照，再本地重传。
 * 上传成功后由 `handleFileSettled` 决定追加入库 + 回写。
 */
const retryUploadTaskItems = async (taskId: string, uids: string[]) => {
  const task = getTask(taskId);
  if (!task) return;

  const targets = uids
    .map((uid) => task.files.find((file) => file.uid === uid))
    .filter(
      (file): file is UploadTaskFile => Boolean(file) && isFileRetryable(file as UploadTaskFile)
    );
  if (targets.length === 0) return;

  const itemIds = targets
    .map((file) => file.itemId)
    .filter((itemId): itemId is string => Boolean(itemId));
  if (itemIds.length === 0) return;

  const store = useUploadTaskStore.getState();

  /**
   * 重试前置：先把这批失败明细回写服务端。
   *
   * **后端只接受「失败状态」的明细重试**。长时间断网时整批会本地失败，
   * 而那次回写同样因断网失败（`reportTaskItems` 重试 3 次后放弃），
   * 服务端明细仍停在「待上传」—— 此时直接调 `/retry` 会被拒
   * （「仅失败状态的明细可以重试」），重试流程直接卡死。
   * 所以这里只针对**本次要重试的明细**补一次回写，把它们真正标成失败；
   * 已上报过的明细会被跳过（幂等），不会产生多余请求。
   */
  const flushed = await reportTaskItems(taskId, { onlyUids: targets.map((file) => file.uid) });
  if (!flushed) {
    // 连状态都同步不上去，重试必然也会被拒 —— 给一条能指导用户操作的提示
    toast.error('失败状态同步失败，请检查网络后重试');
    return;
  }

  let resp;
  try {
    const res = await uploadTaskApi.retry({ taskId, itemIds });
    resp = res.data;
    if (!resp) throw new Error(res.msg || '重试失败');
  } catch (error) {
    toast.error((error as { message?: string })?.message || '重试失败');
    return;
  }

  /** 落库目标与设定快照以后端为准（目录可能被移动 / 改名） */
  const briefMap = new Map(resp.items.map((item) => [item.itemId, item]));
  store.patchTask(taskId, {
    status: 'uploading',
    errorMessage: undefined,
    // 明细已回到待上传，幂等标记要放开，等重传结果再重新上报
    serverSynced: false,
    snapshot: {
      ...task.snapshot,
      designerId: resp.designerId || task.snapshot.designerId,
      creativeId: resp.creativeId ?? task.snapshot.creativeId,
      tagIds: resp.tagIds ?? task.snapshot.tagIds,
      folderCategory: resp.folderCategory ?? task.snapshot.folderCategory,
    },
  });

  targets.forEach((file) => {
    const brief = file.itemId ? briefMap.get(file.itemId) : undefined;
    store.patchFile(taskId, file.uid, {
      reported: false,
      targetFolderId: brief?.folderId ?? file.targetFolderId,
    });
    uploadClient.retryFile(file.uid);
  });
};

/**
 * 单文件重试。
 * 仅用于「当前会话内、本地仍持有 File」的失败素材。
 */
export const retryUploadFile = (taskId: string, uid: string) => {
  void retryUploadTaskItems(taskId, [uid]);
};

/** 重试批次内全部可重试的失败文件 */
export const retryAllFailedFiles = (taskId: string) => {
  const task = getTask(taskId);
  if (!task) return;
  const uids = task.files.filter(isFileRetryable).map((file) => file.uid);
  if (uids.length === 0) return;
  void retryUploadTaskItems(taskId, uids);
};

/**
 * 任务级「重试」：
 * - 有可重传的失败素材 → 交给后端置回待上传后重传，成功后自动补入库；
 * - 只是入库失败 → 直接用已成功的素材重调入库接口。
 */
export const retryUploadTask = async (taskId: string) => {
  const task = getTask(taskId);
  if (!task) return;

  const retryable = task.files.filter(isFileRetryable);
  if (retryable.length > 0) {
    await retryUploadTaskItems(
      taskId,
      retryable.map((file) => file.uid)
    );
    return;
  }
  await submitTask(taskId, { append: true });
};

/** 取消单个文件 */
export const cancelUploadFile = (taskId: string, uid: string) => {
  uploadClient.cancelFile(uid);
  useUploadTaskStore.getState().removeFile(taskId, uid);
};

/** 取消整批任务：本地标记取消，并把未完成的明细回写成失败 */
export const cancelUploadTask = async (taskId: string) => {
  const task = getTask(taskId);
  if (!task) return;
  uploadClient.cancelFiles(task.files.map((file) => file.uid));
  useUploadTaskStore.getState().patchTask(taskId, { status: 'canceled', errorMessage: '已取消' });
  await reportTaskItems(taskId);
};

/** 移除批次记录（仅清本地，不影响服务端已归档的任务） */
export const removeUploadTask = (taskId: string) => {
  const task = getTask(taskId);
  if (task) {
    uploadClient.cancelFiles(task.files.map((file) => file.uid));
  }
  useUploadTaskStore.getState().removeTask(taskId);
  submitTriggered.delete(taskId);
};

/** 清空所有已结束（success / failed / canceled）的批次记录，进行中的保留 */
export const clearFinishedUploadTasks = () => {
  const finished = useUploadTaskStore
    .getState()
    .tasks.filter((task) => task.status !== 'uploading' && task.status !== 'saving');
  finished.forEach((task) => submitTriggered.delete(task.taskId));
  useUploadTaskStore.getState().clearFinished();
};

/** 登出时整体重置 */
export const resetUploadTasks = () => {
  uploadClient.reset();
  useUploadTaskStore.getState().reset();
  submitTriggered.clear();
};

/** reconcile 重入保护：多个入口（宿主 + 页面）可能同时触发 */
let reconciling = false;

/**
 * 断点对账：处理「任务没来得及入库 / 回写」的异常场景。
 * 幂等——已全部上报（`serverSynced`）的任务会被 `buildReconcilePlan` 跳过。
 */
export const reconcileUploadTasks = async () => {
  if (reconciling) return;
  reconciling = true;
  try {
    const plan = buildReconcilePlan(useUploadTaskStore.getState().tasks);
    for (const action of plan) {
      if (action.type === 'skip') continue;
      try {
        // 两种情况都走 submitTask：没有可入库内容时它会自动标记失败并按明细回写
        await submitTask(action.taskId, { append: false, silent: true });
      } catch (error) {
        console.warn('[material-upload] 上传任务对账失败', action.taskId, error);
      }
    }
  } finally {
    reconciling = false;
  }
};
